import { useState } from "react";
import { RetrievalMode, ChatModel, ReasoningEffort } from "../../../api/models";
import { appInfo, allProductAutoSelectStaticDefaults, userInfo } from "../../../authConfig";

const parseRuntimeBoolean = (value: unknown, fallback: boolean): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true") return true;
        if (normalized === "false") return false;
    }
    return fallback;
};

const parseRuntimeNumber = (value: unknown, fallback: number, min: number, max: number): number => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return fallback;
    }
    return Math.min(max, Math.max(min, numericValue));
};

const resolveConfiguredRuntimeNumber = (value: unknown, configuredFallback: unknown, min: number, max: number): number => {
    const normalizedConfiguredFallback = parseRuntimeNumber(configuredFallback, min, min, max);
    return parseRuntimeNumber(value, normalizedConfiguredFallback, min, max);
};

/**
 * Custom hook for managing developer settings state
 */
export const useDeveloperSettings = (defaultChatModel: ChatModel) => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [retrieveCount, setRetrieveCount] = useState<number>(5);
    const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>(ReasoningEffort.Low);
    const [onlyCitations, setOnlyCitations] = useState<boolean>(false);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [docTypeFilter, setDocTypeFilter] = useState<string>("External");
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [useAgenticKnowledgebase, setUseAgenticKnowledgebase] = useState<boolean>(appInfo?.use_agentic_knowledgebase === true);
    const [enableAllProductForAllUsers, setEnableAllProductForAllUsers] = useState<boolean>(appInfo?.enable_all_product_for_all_users === true);
    // Per-user personal ALL product flag — defaults to true if field is missing (existing users)
    const [enableAllProductForUser, setEnableAllProductForUser] = useState<boolean>(userInfo?.enable_all_product !== false);
    const [categories, setCategories] = useState<{ key: string; text: string }[]>([]);
    const [doctype, setDocType] = useState<boolean>(false);
    const [agenticRetrievalOutputSize, setAgenticRetrievalOutputSize] = useState<number | undefined>(undefined);
    const [agenticRetrievalDocumentCount, setAgenticRetrievalDocumentCount] = useState<number | undefined>(undefined);
    const [agenticRetrievalSourceDocumentCount, setAgenticRetrievalSourceDocumentCount] = useState<number | undefined>(undefined);
    const [allProductAutoSelectEnabled, setAllProductAutoSelectEnabled] = useState<boolean>(
        parseRuntimeBoolean((appInfo as any)?.all_product_auto_select?.enabled, parseRuntimeBoolean(allProductAutoSelectStaticDefaults.enabled, true))
    );
    const [allProductAutoSelectMinConfidence, setAllProductAutoSelectMinConfidence] = useState<number>(
        resolveConfiguredRuntimeNumber((appInfo as any)?.all_product_auto_select?.min_confidence, allProductAutoSelectStaticDefaults.min_confidence, 0, 100)
    );
    const [allProductAutoSelectMinMargin, setAllProductAutoSelectMinMargin] = useState<number>(
        resolveConfiguredRuntimeNumber((appInfo as any)?.all_product_auto_select?.min_margin, allProductAutoSelectStaticDefaults.min_margin, 0, 100)
    );
    const [allProductAutoSelectMinHits, setAllProductAutoSelectMinHits] = useState<number>(
        resolveConfiguredRuntimeNumber((appInfo as any)?.all_product_auto_select?.min_hits, allProductAutoSelectStaticDefaults.min_hits, 1, 99)
    );
    const [allProductAutoSelectMinAvgRerankerScore, setAllProductAutoSelectMinAvgRerankerScore] = useState<number>(
        resolveConfiguredRuntimeNumber(
            (appInfo as any)?.all_product_auto_select?.min_avg_reranker_score,
            allProductAutoSelectStaticDefaults.min_avg_reranker_score ?? 2.5,
            0,
            10
        )
    );
    const [allProductAutoSelectMinAvgRerankerGap, setAllProductAutoSelectMinAvgRerankerGap] = useState<number>(
        resolveConfiguredRuntimeNumber(
            (appInfo as any)?.all_product_auto_select?.min_avg_reranker_gap,
            allProductAutoSelectStaticDefaults.min_avg_reranker_gap ?? 0.2,
            0,
            10
        )
    );
    const [allProductAutoSelectWeightAverage, setAllProductAutoSelectWeightAverage] = useState<number>(
        parseRuntimeNumber((appInfo as any)?.all_product_auto_select?.weight_average, 0.45, 0, 1)
    );
    const [allProductAutoSelectWeightPeak, setAllProductAutoSelectWeightPeak] = useState<number>(
        parseRuntimeNumber((appInfo as any)?.all_product_auto_select?.weight_peak, 0.1, 0, 1)
    );
    const [allProductAutoSelectWeightVolume, setAllProductAutoSelectWeightVolume] = useState<number>(
        parseRuntimeNumber((appInfo as any)?.all_product_auto_select?.weight_volume, 0.45, 0, 1)
    );

    return {
        isConfigPanelOpen,
        setIsConfigPanelOpen,
        promptTemplate,
        setPromptTemplate,
        retrieveCount,
        setRetrieveCount,
        reasoningEffort,
        setReasoningEffort,
        onlyCitations,
        setOnlyCitations,
        retrievalMode,
        setRetrievalMode,
        docTypeFilter,
        setDocTypeFilter,
        useSemanticRanker,
        setUseSemanticRanker,
        shouldStream,
        setShouldStream,
        useSemanticCaptions,
        setUseSemanticCaptions,
        excludeCategory,
        setExcludeCategory,
        useSuggestFollowupQuestions,
        setUseSuggestFollowupQuestions,
        useOidSecurityFilter,
        setUseOidSecurityFilter,
        useGroupsSecurityFilter,
        setUseGroupsSecurityFilter,
        useAgenticKnowledgebase,
        setUseAgenticKnowledgebase,
        enableAllProductForAllUsers,
        setEnableAllProductForAllUsers,
        enableAllProductForUser,
        setEnableAllProductForUser,
        categories,
        setCategories,
        doctype,
        setDocType,
        agenticRetrievalOutputSize,
        setAgenticRetrievalOutputSize,
        agenticRetrievalDocumentCount,
        setAgenticRetrievalDocumentCount,
        agenticRetrievalSourceDocumentCount,
        setAgenticRetrievalSourceDocumentCount,
        allProductAutoSelectEnabled,
        setAllProductAutoSelectEnabled,
        allProductAutoSelectMinConfidence,
        setAllProductAutoSelectMinConfidence,
        allProductAutoSelectMinMargin,
        setAllProductAutoSelectMinMargin,
        allProductAutoSelectMinHits,
        setAllProductAutoSelectMinHits,
        allProductAutoSelectMinAvgRerankerScore,
        setAllProductAutoSelectMinAvgRerankerScore,
        allProductAutoSelectMinAvgRerankerGap,
        setAllProductAutoSelectMinAvgRerankerGap,
        allProductAutoSelectWeightAverage,
        setAllProductAutoSelectWeightAverage,
        allProductAutoSelectWeightPeak,
        setAllProductAutoSelectWeightPeak,
        allProductAutoSelectWeightVolume,
        setAllProductAutoSelectWeightVolume
    };
};
